import { useEffect, useState } from 'react';
import { DhCell, DhPlay, DhSpin, DhSym, DH_PAYLINES } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { dogHouseApi } from '../api/doghouse';
import { DogSymbol } from '../components/DogSymbol';
import { ReelGrid } from '../components/ReelGrid';
import { Icon } from '../components/Icon';
import { sound } from '../lib/sound';
import { telegram } from '../lib/telegram';
import { formatTon } from '../lib/format';
import { replayLink } from '../lib/appConfig';

const COLS = 5;
const ROWS = 3;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const LOWS: DhSym[] = ['l1', 'l2', 'l3', 'l4', 'h1', 'h2', 'h3', 'h4'];
const randCell = (): DhCell => ({ s: LOWS[Math.floor(Math.random() * LOWS.length)] });
const STOP_MS = COLS * 150 + 350;

function displayGrid(): DhCell[][] {
  return Array.from({ length: COLS }, () => Array.from({ length: ROWS }, randCell));
}
function winCellsOf(spin: DhSpin): Set<string> {
  const set = new Set<string>();
  spin.lineWins.forEach((w) => {
    const rows = DH_PAYLINES[w.line];
    for (let reel = 0; reel < w.count; reel++) set.add(`${reel}-${rows[reel]}`);
  });
  return set;
}

export function DogHouseScreen({
  onBack,
  replay,
  shareId,
}: {
  onBack: () => void;
  replay?: DhPlay;
  shareId?: number;
}) {
  const balance = useAppStore((s) => s.balance);
  const setBalance = useAppStore((s) => s.setBalance);
  const showToast = useAppStore((s) => s.showToast);

  const [grid, setGrid] = useState<DhCell[][]>(() => displayGrid());
  const [winCells, setWinCells] = useState<Set<string>>(new Set());
  const [spinning, setSpinning] = useState(false);
  const [amount, setAmount] = useState('20');
  const [winText, setWinText] = useState<string | null>(null);
  const [freeBadge, setFreeBadge] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(true);

  const isReplay = !!replay;
  const balanceNum = parseFloat(balance) || 0;
  const amountNum = parseFloat(amount) || 0;
  const canSpin = !spinning && amountNum > 0 && amountNum <= balanceNum;

  const reveal = (spin: DhSpin) => {
    setGrid(spin.grid);
    setWinCells(winCellsOf(spin));
    setSpinning(false);
    if (spin.spinWin > 0) sound.win();
  };

  const animate = async (play: DhPlay) => {
    reveal(play.base);
    if (play.base.spinWin > 0) setWinText(`+${formatTon(play.base.spinWin)}`);
    await sleep(STOP_MS);

    if (play.freeSpinsAwarded > 0) {
      sound.freeSpins();
      setFreeBadge(`STICKY ФРИСПИНЫ ×${play.freeSpinsAwarded}`);
      await sleep(800);
      let acc = play.base.spinWin;
      for (let i = 0; i < play.free.length; i++) {
        setFreeBadge(`ФРИСПИН ${i + 1}/${play.free.length}`);
        setWinCells(new Set());
        setSpinning(true);
        sound.spin();
        await sleep(500);
        acc += play.free[i].spinWin;
        reveal(play.free[i]);
        if (play.free[i].spinWin > 0) setWinText(`STICKY · всего +${formatTon(acc)}`);
        await sleep(STOP_MS);
      }
      setFreeBadge(null);
    }
  };

  useEffect(() => {
    if (!replay) return;
    let cancelled = false;
    (async () => {
      sound.resume();
      sound.spin();
      setWinText(null);
      setFreeBadge(null);
      setWinCells(new Set());
      setSpinning(true);
      await sleep(850);
      if (cancelled) return;
      await animate(replay);
      if (cancelled) return;
      if (replay.totalWin > 0) {
        setWinText(`+${formatTon(replay.totalWin)}`);
        sound.coin();
      }
      setSpinning(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSpin = async (buy = false) => {
    const cost = buy ? amountNum * 100 : amountNum;
    if (spinning || amountNum <= 0) return;
    if (cost > balanceNum) {
      showToast('Недостаточно средств', true);
      return;
    }
    telegram.haptic(buy ? 'heavy' : 'medium');
    sound.resume();
    sound.spin();
    setWinText(null);
    setFreeBadge(buy ? 'БОНУС КУПЛЕН!' : null);
    setWinCells(new Set());
    setSpinning(true);

    const start = performance.now();
    try {
      const res = await dogHouseApi.spin(amount, buy);
      const elapsed = performance.now() - start;
      if (elapsed < 850) await sleep(850 - elapsed);

      await animate(res.play);

      const total = parseFloat(res.totalWin);
      setBalance(res.newBalance);
      if (total > 0) {
        setWinText(`+${formatTon(res.totalWin)}`);
        if (total >= cost * 2) sound.bigWin();
        else sound.coin();
        showToast(`Выигрыш +${formatTon(res.totalWin)}`);
        telegram.notify('success');
      } else {
        telegram.notify('error');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Ошибка спина';
      showToast(Array.isArray(msg) ? msg[0] : msg, true);
    } finally {
      setSpinning(false);
      setFreeBadge(null);
    }
  };
  const spin = () => runSpin(false);

  const setAmt = (v: number) => {
    const c = Math.max(0, Math.min(v, balanceNum || v));
    setAmount(c % 1 === 0 ? String(c) : c.toFixed(2));
  };

  return (
    <>
      <div className="page-head">
        <button className="icon-btn" onClick={onBack} title="Назад">
          <Icon name="back" size={18} />
        </button>
        <span className="title">{isReplay ? 'Повтор · Дом Псов' : 'Дом Псов'}</span>
        <button
          className={`icon-btn ${soundOn ? 'accent' : ''}`}
          style={{ marginLeft: 'auto' }}
          onClick={() => setSoundOn(sound.toggle())}
          title="Звук"
        >
          <Icon name="bolt" size={16} />
        </button>
      </div>

      <div className="card dh-board">
        {freeBadge && <div className="oly-free">{freeBadge}</div>}
        <ReelGrid
          reels={COLS}
          rows={ROWS}
          grid={grid}
          spinning={spinning}
          winCells={winCells}
          randomCell={randCell}
          renderCell={(cell) => <DogSymbol cell={cell} />}
          extraCellClass={(cell) => (cell.sticky ? 'sticky' : '')}
        />
        <div className={`slot-win ${winText ? 'show' : ''}`}>{winText ?? ' '}</div>
      </div>

      {isReplay ? (
        <div className="card replay-foot">
          <span className="replay-tag">ПОВТОР ВЫИГРЫША</span>
          <button
            className="btn"
            disabled={spinning}
            onClick={() =>
              telegram.share(
                `Зацени реплей: +${formatTon(replay!.totalWin)} в «Доме Псов» — 1Zov`,
                shareId ? replayLink(shareId) : undefined
              )
            }
          >
            Поделиться
          </button>
          <button className="btn ghost" onClick={onBack}>К повторам</button>
        </div>
      ) : (
        <div className="card bet">
          <div className="label-row">
            <span className="label">Ставка (на 20 линий)</span>
            <span className="muted">баланс {formatTon(balance)}</span>
          </div>
          <div className="field">
            <span className="gem">◆</span>
            <input
              inputMode="decimal"
              value={amount}
              disabled={spinning}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            />
            <div className="mini-actions">
              <button onClick={() => setAmt(amountNum / 2)} disabled={spinning}>½</button>
              <button onClick={() => setAmt(amountNum * 2)} disabled={spinning}>2×</button>
              <button onClick={() => setAmt(balanceNum)} disabled={spinning}>MAX</button>
            </div>
          </div>
          <button className={`btn ${spinning ? 'loading' : ''}`} disabled={!canSpin} onClick={spin}>
            {spinning ? <span className="spin" /> : 'Крутить'}
          </button>
          <button
            className="btn buy-bonus"
            disabled={spinning || amountNum <= 0 || amountNum * 100 > balanceNum}
            onClick={() => runSpin(true)}
          >
            Купить бонус · {formatTon((amountNum * 100).toFixed(2))}
          </button>
          <p className="muted">wild ×2/×3 на средних барабанах · покупка бонуса = сразу фриспины со sticky-wild</p>
        </div>
      )}
    </>
  );
}
